/**
 * Page /service-technique/arretes — Liste des arrêtés municipaux.
 *
 * Vue desktop pour admin/manager : tableau des arrêtés avec filtres statut/type,
 * bouton "Nouvel arrêté" + accès au détail.
 *
 * Refonte R6.2 (mai 2026) :
 *   - Topbar glass cohérent + sub-nav avec tabs en pills glass
 *   - LogoMark FileSignature accent tech au-dessus du titre
 *   - Card filtres glass + Card liste avec items en hover lift
 *   - Empty state avec LogoMark + texte d'encouragement
 *
 * Logique métier 100% préservée :
 *   - api.serviceTechnique.arretes.list({statut, type, limit: 100}) avec filtres réactifs
 *   - STATUTS map (genere, relecture_juridique, a_signer, signe, publie, annule)
 *   - TYPES map (voirie, securite, urbanisme, evenement, autre)
 *   - Reload useEffect sur changement filtres
 *   - Sub-nav avec tabs (Planning / Fiches incident / Arrêtés actif / Véhicules disabled)
 */

import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, ApiException } from '@/lib/api';
import { useAuth } from '@/lib/auth-context';
import { Button, Badge, Card, Alert, LogoMark, SectionEyebrow } from '@/components/ui';
import { AppHeader } from '@/components/AppHeader';
import { ServiceTechniqueNav } from '@/components/ServiceTechniqueNav';
import {
  Landmark, FileSpreadsheet, Plus, ChevronRight,
  CheckCircle2, Clock, Edit, FileSignature, FileX,
  AlertCircle, ArrowLeft,
} from 'lucide-react';

const STATUTS: Record<string, { label: string; tone: 'neutral' | 'warning' | 'success' | 'urgent'; icon: typeof Edit }> = {
  genere:              { label: 'Brouillon',           tone: 'neutral', icon: Edit },
  relecture_juridique: { label: 'Relecture juridique', tone: 'warning', icon: Clock },
  a_signer:            { label: 'À signer',            tone: 'warning', icon: FileSignature },
  signe:               { label: 'Signé',               tone: 'success', icon: CheckCircle2 },
  publie:              { label: 'Publié',              tone: 'success', icon: CheckCircle2 },
  annule:              { label: 'Annulé',              tone: 'urgent',  icon: FileX },
};

// Les types affichés viennent des ACTES eux-mêmes, plus d'une table figée : le
// type est désormais la catégorie du catalogue (mig 264), et cette liste de cinq
// valeurs n'avait plus aucun rapport avec ce que la commune produit.

export function ArretesListPage() {
  const navigate = useNavigate();
  const { tenant, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [items, setItems] = useState<Awaited<ReturnType<typeof api.serviceTechnique.arretes.list>>['items']>([]);
  /** Les types réellement présents dans les actes de la brique. */
  const typesPresents = [...new Set(items.map((a) => a.type).filter(Boolean))].sort();
  const [total, setTotal] = useState(0);
  const [filterStatut, setFilterStatut] = useState<string>('');
  const [filterType, setFilterType] = useState<string>('');

  async function reload() {
    setLoading(true);
    setError(null);
    try {
      const res = await api.serviceTechnique.arretes.list({
        statut: filterStatut || undefined,
        type: filterType || undefined,
        limit: 100,
      });
      setItems(res.items);
      setTotal(res.total);
    } catch (err) {
      setError(err instanceof ApiException ? err.payload.error : 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void reload(); /* eslint-disable-next-line */ }, [filterStatut, filterType]);

  return (
    <div className="min-h-screen text-ink">
      {/* ===== Topbar glass cohérent ===== */}
      <AppHeader module="Service technique" accent="tech" />

      {/* ===== Sub-nav : tabs en pills glass ===== */}
      <nav
        aria-label="Navigation Service technique"
        className="border-b border-border backdrop-blur-glass px-4 py-2"
        style={{ backgroundImage: 'linear-gradient(180deg, oklch(var(--c-bg-2) / 0.3), oklch(var(--c-bg) / 0.4))' }}
      >
        <div>
          {/* Le bouton d'action quitte la barre de NAVIGATION : elle sert à
              naviguer, pas à agir. Il rejoint sa place au-dessus de la liste,
              comme côté RH — les deux briques se ressemblent enfin. */}
          <ServiceTechniqueNav active="arretes" />
        </div>
      </nav>

      <main id="main-content" className="mx-auto max-w-6xl px-4 py-6 animate-fade-in">
        <div className="mb-4 flex justify-end">
          <Button type="button" variant="primary"
            onClick={() => navigate('/service-technique/arretes/nouveau')}>
            <Plus className="h-4 w-4" aria-hidden="true" /> Nouvel arrêté
          </Button>
        </div>

        {/* En-tête de page */}
        <div className="flex items-center gap-3 mb-5">
          <LogoMark icon={FileSignature} accent="tech" size="lg" />
          <div>
            <SectionEyebrow as="div">Service technique</SectionEyebrow>
            <h1 className="mt-0.5 font-display text-xl font-semibold tracking-r-snug text-ink sm:text-2xl">
              Arrêtés municipaux
            </h1>
            <p className="mt-0.5 text-sm text-ink-muted">
              {total} arrêté{total > 1 ? 's' : ''} au total
            </p>
          </div>
        </div>

        {error && (
          <Alert variant="error" icon={AlertCircle} className="mb-3">
            {error}
          </Alert>
        )}

        {/* Filtres en Card glass */}
        <Card variant="glass" padded className="mb-3">
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-xs uppercase tracking-r-eyebrow text-ink-subtle">Filtrer :</span>
            <select
              value={filterStatut}
              onChange={(e) => setFilterStatut(e.target.value)}
              className="h-9 rounded-r-lg border border-border bg-surface/60 backdrop-blur-glass px-2.5 text-sm text-ink shadow-soft transition-all focus:border-tech focus:outline-none focus:ring-2 focus:ring-tech focus:ring-offset-2 focus:ring-offset-bg hover:border-border-strong"
              aria-label="Filtrer par statut"
            >
              <option value="">Tous les statuts</option>
              {Object.entries(STATUTS).map(([v, s]) => (
                <option key={v} value={v}>{s.label}</option>
              ))}
            </select>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="h-9 rounded-r-lg border border-border bg-surface/60 backdrop-blur-glass px-2.5 text-sm text-ink shadow-soft transition-all focus:border-tech focus:outline-none focus:ring-2 focus:ring-tech focus:ring-offset-2 focus:ring-offset-bg hover:border-border-strong"
              aria-label="Filtrer par type"
            >
              <option value="">Tous les types</option>
              {typesPresents.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>
        </Card>

        {loading ? (
          <Card variant="glass" padded>
            <div className="py-8 text-center">
              <div
                className="mx-auto h-8 w-8 animate-spin rounded-full border-2 border-tech border-t-transparent"
                role="status"
                aria-label="Chargement"
              />
              <p className="mt-4 text-sm text-ink-muted">Chargement des arrêtés…</p>
            </div>
          </Card>
        ) : items.length === 0 ? (
          <Card variant="glass" padded>
            <div className="py-8 text-center">
              <div className="flex justify-center mb-3">
                <LogoMark icon={FileSpreadsheet} accent="tech" size="lg" />
              </div>
              <h2 className="font-display text-base font-semibold tracking-r-snug text-ink">
                Aucun arrêté
              </h2>
              <p className="mt-1 text-sm text-ink-muted">
                Créez votre premier arrêté depuis le bouton « Nouvel arrêté » ou depuis un signalement de voirie.
              </p>
            </div>
          </Card>
        ) : (
          <div className="space-y-2">
            {items.map((a) => {
              const s = STATUTS[a.statut] ?? STATUTS.genere!;
              const Icon = s.icon;
              return (
                <Link
                  key={a.id}
                  to={`/service-technique/arretes/${a.id}`}
                  className="group block w-full rounded-r-xl border border-border backdrop-blur-glass px-4 py-3 shadow-soft transition-all duration-200 ease-out-expo hover:-translate-y-0.5 hover:border-tech/30 hover:shadow-glass-hi focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tech focus-visible:ring-offset-2 focus-visible:ring-offset-bg"
                  style={{
                    backgroundImage: 'linear-gradient(90deg, oklch(var(--c-surface) / 0.55), oklch(var(--c-bg-2) / 0.45))',
                  }}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className="inline-grid h-9 w-9 place-items-center rounded-r-lg bg-tech/15 text-tech-fg flex-shrink-0"
                      aria-hidden="true"
                    >
                      <Icon className="h-4 w-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-mono text-xs text-ink-muted">{a.numero}</span>
                        <Badge variant={s.tone} size="sm">{s.label}</Badge>
                        <Badge variant="neutral" size="sm">{a.type}</Badge>
                      </div>
                      <p className="mt-1 truncate font-medium text-ink">{a.titre}</p>
                      {a.objet && <p className="truncate text-xs text-ink-subtle">{a.objet}</p>}
                    </div>
                    <div className="text-right text-xs text-ink-subtle flex-shrink-0 hidden sm:block">
                      <div>Généré {new Date(a.genereAt).toLocaleDateString('fr-FR')}</div>
                      {a.signeAt && (
                        <div className="text-success">Signé {new Date(a.signeAt).toLocaleDateString('fr-FR')}</div>
                      )}
                    </div>
                    <ChevronRight className="h-4 w-4 shrink-0 text-ink-dim transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
                  </div>
                </Link>
              );
            })}
          </div>
        )}

        {/* 3.7 — référentiel des adresses de relecture (paramètre ST). */}
        <div className="mt-8">
          </div>
      </main>
    </div>
  );
}

